/*
 * Presento Pronto: Pronto-Rad <pp-wheel> (Markup und Einstellungen in sections/pp-wheel.liquid,
 * Header-Button in snippets/pp-wheel-trigger.liquid, Menue-Kachel in snippets/pp-wheel-menu-item.liquid).
 *
 * Ablauf:
 * - Oeffnen ueber [data-pp-wheel-open] oder jeden Link auf "#pronto-rad" (auch direkt per URL-Hash).
 * - Drehen: Feld gewichtet per Zufall waehlen (crypto), Zustand sofort speichern (Schliessen waehrend des
 *   Drehens gibt keinen zweiten Versuch), Rad per CSS-Transition auf das Feld drehen, dann Gewinn zeigen.
 * - Der Rabattcode wird sofort per /cart/update.js in den Warenkorb gelegt. Shopify behaelt ihn dort auch
 *   unterhalb des Mindestwerts und wendet ihn an, sobald der Mindestwert erreicht ist.
 * - Fortschritt: Header-Button, Dialog und eine Leiste vor dem Warenkorb-Booster (.pp-cb) zeigen
 *   "noch X bis zu deinem Gewinn". Die Leiste wird eingefuegt, weil Horizon den Warenkorb nach jeder
 *   Aenderung per Morph neu aufbaut; ein MutationObserver setzt sie danach wieder ein.
 * - Nach Ablauf der Reservierung wird der Code wieder aus dem Warenkorb genommen.
 * - Zustand pro Browser in localStorage (bewusst: kein Konto noetig). ?pp-wheel=reset setzt zurueck.
 * - Bewegung: bei prefers-reduced-motion kurzer Dreh ohne Schwung und ohne Konfetti.
 */

const STORAGE_KEY = 'pp-wheel:v1';
const OPEN_HASH = '#pronto-rad';
const CART_EVENTS = ['shopify:cart:lines-update', 'shopify:cart:discount-update'];
const CONFETTI_COLORS = ['var(--pp-cta)', 'var(--pp-red)', 'var(--pp-blue)', 'var(--pp-pink)', 'var(--pp-gold)'];

const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

function readState() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
  } catch {
    return null;
  }
}

function writeState(state) {
  try {
    if (state) localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* privater Modus: Gewinn gilt dann nur fuer diese Seite */
  }
}

function randomUnit() {
  const buffer = new Uint32Array(1);
  crypto.getRandomValues(buffer);
  return buffer[0] / 2 ** 32;
}

function pad(value) {
  return String(value).padStart(2, '0');
}

function formatCountdown(ms) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  return `${pad(hours)}:${pad(minutes)}:${pad(total % 60)}`;
}

async function fetchCart() {
  const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error('cart');
  return response.json();
}

function cartCodes(cart) {
  return (cart && Array.isArray(cart.discount_codes) ? cart.discount_codes : []).map((entry) => entry.code);
}

async function setCartCodes(codes) {
  const response = await fetch(`${root}cart/update.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ discount: codes.join(',') }),
  });
  if (!response.ok) throw new Error('discount');
  return response.json();
}

function sameCode(a, b) {
  return String(a).toLowerCase() === String(b).toLowerCase();
}

function track(name, detail) {
  try {
    window.Shopify?.analytics?.publish?.(name, detail);
  } catch {
    /* Analytics ist optional */
  }
}

if (!customElements.get('pp-wheel')) {
  customElements.define(
    'pp-wheel',
    class PpWheel extends HTMLElement {
      #prizes = [];
      #state = null;
      #cart = null;
      #rotation = 0;
      #spinning = false;
      #tick = 0;
      #observer = null;
      #observerQueued = false;
      #cartTimer = 0;
      #money = null;

      connectedCallback() {
        try {
          this.#prizes = JSON.parse(this.querySelector('[data-config]')?.textContent || '[]');
        } catch {
          this.#prizes = [];
        }
        if (this.#prizes.length < 2) return;

        this.dialog = this.querySelector('dialog');
        this.disc = this.querySelector('[data-disc]');
        this.stage = this.querySelector('.pp-wheel__stage');
        this.validMs = Number(this.dataset.validHours || 48) * 3600e3;
        this.cooldownMs = Number(this.dataset.cooldownDays || 30) * 86400e3;
        const currency = this.dataset.currency || 'EUR';
        const whole = new Intl.NumberFormat('de-DE', { style: 'currency', currency, maximumFractionDigits: 0 });
        const exact = new Intl.NumberFormat('de-DE', { style: 'currency', currency });
        // "30 €" statt "30,00 €", aber "12,40 €" bei Cent-Betraegen
        this.#money = { format: (value) => (Number.isInteger(value) ? whole : exact).format(value) };

        const params = new URLSearchParams(location.search);
        if (params.get('pp-wheel') === 'reset') writeState(null);
        this.#state = readState();

        this.#renderOdds();
        this.#bind();
        this.#refresh();
        this.#syncCart();

        if (location.hash === OPEN_HASH) this.open();
      }

      disconnectedCallback() {
        clearInterval(this.#tick);
        this.#observer?.disconnect();
        document.removeEventListener('click', this.#onDocumentClick);
        CART_EVENTS.forEach((name) => document.removeEventListener(name, this.#onCartEvent));
      }

      /* ---------- Oeffnen / Schliessen ---------- */

      open() {
        if (!this.dialog || this.dialog.open) return;
        this.#refresh();
        this.dialog.showModal();
        this.#startTick();
        track('pp_wheel_open', { state: this.#phase() });
        this.#syncCart();
      }

      close() {
        this.dialog?.close();
      }

      #bind() {
        document.addEventListener('click', this.#onDocumentClick);
        window.addEventListener('hashchange', () => {
          if (location.hash === OPEN_HASH) this.open();
        });
        CART_EVENTS.forEach((name) => document.addEventListener(name, this.#onCartEvent));
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') this.#refresh();
        });

        this.querySelector('[data-close]')?.addEventListener('click', () => this.close());
        this.dialog.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.close();
        });
        this.dialog.addEventListener('close', () => {
          this.#startTick();
          if (location.hash === OPEN_HASH) history.replaceState(null, '', location.pathname + location.search);
        });
        this.querySelector('[data-spin]')?.addEventListener('click', () => this.#spin());
        this.querySelector('[data-copy]')?.addEventListener('click', (event) => this.#copy(event.currentTarget));
        this.querySelector('[data-cta]')?.addEventListener('click', () => this.#applyCode());
      }

      #onDocumentClick = (event) => {
        const target = event.target instanceof Element ? event.target : null;
        const opener = target?.closest(`[data-pp-wheel-open], a[href$="${OPEN_HASH}"]`);
        if (!opener || this.contains(opener)) return;
        event.preventDefault();
        this.open();
      };

      #onCartEvent = () => {
        // Horizon morpht den Warenkorb erst nach dem Event; danach neu lesen
        clearTimeout(this.#cartTimer);
        this.#cartTimer = setTimeout(() => this.#syncCart(), 350);
      };

      /* ---------- Zustand ---------- */

      #phase() {
        const state = this.#state;
        if (!state) return 'ready';
        const now = Date.now();
        if (now < state.expires) return 'won';
        if (now < state.spunAt + this.cooldownMs) return 'cooldown';
        return 'ready';
      }

      #refresh() {
        const phase = this.#phase();

        // Reservierung abgelaufen: Code einmalig wieder aus dem Warenkorb nehmen
        if (phase !== 'won' && this.#state && !this.#state.cleaned) this.#removeExpiredCode(this.#state);
        if (phase === 'ready' && this.#state) {
          // Pause vorbei: neuer Dreh erlaubt
          this.#state = null;
          writeState(null);
        }

        if (!this.#spinning) {
          this.querySelectorAll('[data-view]').forEach((view) => {
            view.hidden = view.dataset.view !== phase;
          });
        }

        if (phase === 'won') this.#renderWon();
        if (phase === 'cooldown') {
          const next = new Date(this.#state.spunAt + this.cooldownMs);
          const slot = this.querySelector('[data-next-date]');
          if (slot) slot.textContent = next.toLocaleDateString('de-DE', { day: 'numeric', month: 'long' });
        }

        this.#renderTriggers(phase);
        this.#renderCartBars(phase);
        this.#watchCart(phase === 'won');
        this.#startTick();
      }

      #startTick() {
        clearInterval(this.#tick);
        if (this.#phase() !== 'won') return;
        // Sekundentakt nur bei offenem Dialog, sonst reicht die Minute fuer den Header
        const every = this.dialog?.open ? 1000 : 30000;
        this.#tick = setInterval(() => {
          if (this.#phase() !== 'won') this.#refresh();
          else this.#renderCountdown();
        }, every);
      }

      /* ---------- Drehen ---------- */

      #pick() {
        const total = this.#prizes.reduce((sum, prize) => sum + Math.max(0, Number(prize.weight) || 0), 0);
        let roll = randomUnit() * total;
        for (let index = 0; index < this.#prizes.length; index += 1) {
          roll -= Math.max(0, Number(this.#prizes[index].weight) || 0);
          if (roll < 0) return index;
        }
        return this.#prizes.length - 1;
      }

      #spin() {
        if (this.#spinning || this.#phase() !== 'ready') return;
        this.#spinning = true;

        const index = this.#pick();
        const prize = this.#prizes[index];
        const now = Date.now();
        this.#state = {
          index,
          code: prize.code,
          label: prize.label,
          title: prize.title,
          note: prize.note || '',
          min: Number(prize.min) || 0,
          spunAt: now,
          expires: now + this.validMs,
        };
        writeState(this.#state);
        track('pp_wheel_spin', { code: prize.code, min: prize.min });

        const button = this.querySelector('[data-spin]');
        const label = this.querySelector('[data-spin-label]');
        if (button) button.disabled = true;
        if (label) label.textContent = 'Das Rad dreht …';

        const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
        const seg = 360 / this.#prizes.length;
        const center = (index + 0.5) * seg;
        const jitter = (randomUnit() - 0.5) * seg * 0.6;
        const base = Math.ceil(this.#rotation / 360) * 360;
        const turns = reduce ? 1 : 6;
        const duration = reduce ? 700 : 5200;
        this.#rotation = base + turns * 360 + (360 - center) - jitter;

        this.disc.style.setProperty('--pp-wheel-duration', `${duration}ms`);
        this.disc.classList.add('is-spinning');
        this.stage?.classList.add('is-spinning');
        // Einen Frame warten, damit die Transition sicher greift
        requestAnimationFrame(() => {
          this.disc.style.setProperty('--pp-wheel-rot', `${this.#rotation}deg`);
        });

        // Code schon waehrend des Drehens in den Warenkorb legen
        this.#applyCode();

        let done = false;
        const finish = () => {
          if (done) return;
          done = true;
          this.disc.classList.remove('is-spinning');
          this.stage?.classList.remove('is-spinning');
          this.#spinning = false;
          if (button) button.disabled = false;
          this.#refresh();
          this.querySelector('[data-won-title]')?.focus();
          if (!reduce) this.#confetti();
        };
        this.disc.addEventListener('transitionend', finish, { once: true });
        setTimeout(finish, duration + 300);
      }

      /* ---------- Warenkorb ---------- */

      async #applyCode() {
        const state = this.#state;
        if (!state?.code) return;
        try {
          const cart = this.#cart || (await fetchCart());
          const others = cartCodes(cart).filter((code) => !this.#isWheelCode(code));
          const updated = await setCartCodes([...others, state.code]);
          this.#cart = updated;
          this.#renderProgress();
        } catch {
          /* Code bleibt sichtbar und kann an der Kasse eingegeben werden */
        }
      }

      async #removeExpiredCode(state) {
        state.cleaned = true;
        writeState(state);
        try {
          const cart = await fetchCart();
          const codes = cartCodes(cart);
          if (codes.some((code) => sameCode(code, state.code))) {
            this.#cart = await setCartCodes(codes.filter((code) => !sameCode(code, state.code)));
          }
        } catch {
          /* naechster Seitenaufruf versucht es nicht erneut; der Code ist dann ohnehin nur manuell nutzbar */
        }
      }

      #isWheelCode(code) {
        return this.#prizes.some((prize) => sameCode(prize.code, code));
      }

      async #syncCart() {
        if (this.#phase() !== 'won') return;
        try {
          this.#cart = await fetchCart();
        } catch {
          return;
        }
        this.#renderProgress();
      }

      #watchCart(active) {
        if (!active) {
          this.#observer?.disconnect();
          this.#observer = null;
          return;
        }
        if (this.#observer) return;
        this.#observer = new MutationObserver(() => {
          if (this.#observerQueued) return;
          this.#observerQueued = true;
          // setTimeout statt requestAnimationFrame: laeuft auch in Hintergrund-Tabs (rAF pausiert dort)
          setTimeout(() => {
            this.#observerQueued = false;
            this.#renderCartBars(this.#phase(), true);
          }, 0);
        });
        this.#observer.observe(document.body, { childList: true, subtree: true });
      }

      /* ---------- Darstellung ---------- */

      #progress() {
        const state = this.#state;
        if (!state || !this.#cart) return null;
        const subtotal = Number(this.#cart.items_subtotal_price) || 0;
        const missing = Math.max(0, state.min - subtotal);
        const percent = state.min > 0 ? Math.min(100, Math.round((subtotal / state.min) * 100)) : 100;
        // Shopify meldet je Code, ob er greift. Erreicht, aber nicht angewendet (z. B. Code fehlt im Admin
        // oder ist nicht kombinierbar): nicht "aktiv" behaupten, sondern auf die Kasse verweisen.
        const entry = (this.#cart.discount_codes || []).find((item) => sameCode(item.code, state.code));
        const rejected = missing === 0 && entry?.applicable === false;
        return {
          subtotal,
          missing,
          percent,
          met: missing === 0 && !rejected,
          rejected,
          items: Number(this.#cart.item_count) || 0,
        };
      }

      #renderWon() {
        const state = this.#state;
        // Nach Neuladen steht das Rad auf dem gewonnenen Feld
        if (!this.#spinning && this.#rotation === 0 && Number.isInteger(state.index)) {
          const seg = 360 / this.#prizes.length;
          this.#rotation = 360 - (state.index + 0.5) * seg;
          this.disc?.style.setProperty('--pp-wheel-rot', `${this.#rotation}deg`);
        }
        const minText = state.min > 0 ? `ab ${this.#money.format(state.min / 100)} Bestellwert` : 'ohne Mindestbestellwert';
        this.#setText('[data-won-title]', state.title);
        this.#setText('[data-won-detail]', `Dein Gewinn gilt ${minText} und liegt schon in deinem Warenkorb.`);
        this.#setText('[data-won-code]', state.code);
        const note = this.querySelector('[data-won-note]');
        if (note) {
          note.textContent = state.note;
          note.hidden = !state.note;
        }
        this.#renderCountdown();
        this.#renderProgress();
      }

      #renderCountdown() {
        if (!this.#state) return;
        const left = this.#state.expires - Date.now();
        this.#setText('[data-countdown]', formatCountdown(left));
        document.querySelectorAll('.pp-wheel-trigger').forEach((trigger) => {
          trigger.title = `Gewinn reserviert noch ${formatCountdown(left).slice(0, 5)} Std.`;
        });
        document.querySelectorAll('[data-pp-wheel-cart-timer]').forEach((slot) => {
          slot.textContent = formatCountdown(left).slice(0, 5);
        });
      }

      #renderProgress() {
        const progress = this.#progress();
        const state = this.#state;
        const box = this.querySelector('[data-progress]');
        const cta = this.querySelector('[data-cta]');
        const cartLink = this.querySelector('[data-cart-link]');

        if (box && progress && state) {
          box.hidden = false;
          box.style.setProperty('--pp-wheel-progress', `${progress.percent}%`);
          this.#setText(
            '[data-progress-text]',
            progress.met
              ? 'Mindestwert erreicht: Dein Gewinn wird an der Kasse abgezogen.'
              : progress.rejected
                ? `Mindestwert erreicht: Gib an der Kasse den Code ${state.code} ein.`
                : `Noch ${this.#money.format(progress.missing / 100)} bis zu deinem Gewinn.`
          );
        }
        if (cta && cartLink && progress) {
          cartLink.hidden = progress.items === 0;
          if (progress.met) {
            cta.textContent = 'Zur Kasse mit Gewinn';
            cta.setAttribute('href', this.dataset.cartUrl || '/cart');
            cartLink.hidden = true;
          } else {
            cta.textContent = progress.items > 0 ? 'Weiter stöbern' : 'Gewinn einlösen';
            cta.setAttribute('href', this.dataset.shopUrl || '/collections/all');
          }
        }
        this.#renderCartBars(this.#phase());
      }

      #renderTriggers(phase) {
        const label = this.dataset.triggerLabel || 'Pronto-Rad';
        document.querySelectorAll('.pp-wheel-trigger').forEach((trigger) => {
          const text = trigger.querySelector('.pp-wheel-trigger__label');
          const won = phase === 'won';
          trigger.classList.toggle('is-won', won);
          if (text) text.textContent = won ? `${this.#state.label} gesichert` : label;
          trigger.setAttribute('aria-label', won ? `Pronto-Rad: ${this.#state.title} gesichert` : label);
          if (!won) trigger.removeAttribute('title');
        });
        document.querySelectorAll('[data-pp-wheel-status]').forEach((status) => {
          status.textContent = phase === 'won' ? `${this.#state.title} gesichert` : 'Jedes Feld gewinnt';
        });
        if (phase === 'won') this.#renderCountdown();
      }

      /** @param {boolean} [onlyMissing] - Nur fehlende Leisten einsetzen (Observer), sonst alle aktualisieren */
      #renderCartBars(phase, onlyMissing = false) {
        const bars = document.querySelectorAll('.pp-wheel-cart');
        if (phase !== 'won') {
          bars.forEach((bar) => bar.remove());
          return;
        }

        const added = [];
        document.querySelectorAll('.pp-cb').forEach((booster) => {
          const previous = booster.previousElementSibling;
          if (previous?.classList.contains('pp-wheel-cart')) return;
          const bar = document.createElement('div');
          bar.className = 'pp-wheel-cart';
          bar.setAttribute('role', 'status');
          bar.innerHTML =
            '<p><span class="pp-wheel-cart__icon" aria-hidden="true">🎁</span><span data-text></span></p>' +
            '<span class="pp-wheel__bar"><span></span></span>';
          booster.before(bar);
          added.push(bar);
        });
        // Eigene Textaenderungen loesen den Observer erneut aus; ohne neue Leiste hier aufhoeren (keine Schleife)
        if (onlyMissing && added.length === 0) return;

        const progress = this.#progress();
        const state = this.#state;
        (onlyMissing ? added : document.querySelectorAll('.pp-wheel-cart')).forEach((bar) => {
          const text = bar.querySelector('[data-text]');
          if (!text) return;
          if (!progress) {
            text.textContent = `Pronto-Rad: ${state.title} ist für dich reserviert.`;
            return;
          }
          bar.classList.toggle('is-met', progress.met);
          bar.style.setProperty('--pp-wheel-progress', `${progress.percent}%`);
          if (progress.rejected) {
            text.textContent = `Pronto-Rad: Gib an der Kasse den Code ${state.code} ein.`;
          } else if (progress.met) {
            text.innerHTML = '';
            text.append(`Pronto-Rad-Gewinn aktiv: ${state.title}. Reserviert noch `);
            const timer = document.createElement('strong');
            timer.dataset.ppWheelCartTimer = '';
            timer.textContent = formatCountdown(state.expires - Date.now()).slice(0, 5);
            text.append(timer, ' Std.');
          } else {
            text.innerHTML = '';
            const missing = document.createElement('strong');
            missing.textContent = this.#money.format(progress.missing / 100);
            text.append('Noch ', missing, ` bis zu deinem Pronto-Rad-Gewinn: ${state.title}`);
          }
        });
      }

      #renderOdds() {
        const list = this.querySelector('[data-odds]');
        if (!list) return;
        const total = this.#prizes.reduce((sum, prize) => sum + Math.max(0, Number(prize.weight) || 0), 0) || 1;
        const merged = new Map();
        this.#prizes.forEach((prize) => {
          const key = `${String(prize.code).toLowerCase()}|${prize.title}`;
          const entry = merged.get(key) || { prize, weight: 0 };
          entry.weight += Math.max(0, Number(prize.weight) || 0);
          merged.set(key, entry);
        });
        list.replaceChildren(
          ...[...merged.values()].map(({ prize, weight }) => {
            const item = document.createElement('li');
            const name = document.createElement('span');
            const min = Number(prize.min) || 0;
            name.textContent = min > 0 ? `${prize.title} (ab ${this.#money.format(min / 100)})` : prize.title;
            const chance = document.createElement('span');
            chance.textContent = `${Math.round((weight / total) * 100)} %`;
            item.append(name, chance);
            return item;
          })
        );
      }

      #setText(selector, value) {
        const node = this.querySelector(selector);
        if (node) node.textContent = value;
      }

      async #copy(button) {
        const code = this.#state?.code;
        if (!code) return;
        try {
          await navigator.clipboard.writeText(code);
          button.textContent = 'Kopiert ✓';
        } catch {
          button.textContent = 'Bitte markieren';
        }
        setTimeout(() => {
          button.textContent = 'Kopieren';
        }, 2000);
      }

      #confetti() {
        const panel = this.querySelector('.pp-wheel__panel');
        if (!panel || !this.stage) return;
        const panelBox = panel.getBoundingClientRect();
        const stageBox = this.stage.getBoundingClientRect();
        const layer = document.createElement('div');
        layer.className = 'pp-wheel__confetti';
        layer.setAttribute('aria-hidden', 'true');
        const originX = stageBox.left - panelBox.left + stageBox.width / 2;
        const originY = stageBox.top - panelBox.top + stageBox.height / 2;
        for (let i = 0; i < 36; i += 1) {
          const piece = document.createElement('i');
          const angle = randomUnit() * Math.PI * 2;
          const distance = 120 + randomUnit() * 220;
          piece.style.left = `${originX}px`;
          piece.style.top = `${originY}px`;
          piece.style.background = CONFETTI_COLORS[i % CONFETTI_COLORS.length];
          piece.style.setProperty('--x', `${Math.cos(angle) * distance}px`);
          piece.style.setProperty('--y', `${Math.sin(angle) * distance + 80}px`);
          piece.style.setProperty('--r', `${Math.round(randomUnit() * 720 - 360)}deg`);
          piece.style.animationDelay = `${Math.round(randomUnit() * 120)}ms`;
          layer.append(piece);
        }
        panel.append(layer);
        setTimeout(() => layer.remove(), 2000);
      }
    }
  );
}
